import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { UserFormData, UsersDataRequestActionTypes } from '../models/user-management';
import { getStatusMessage } from '../../utils/response-code-messages';
import { get } from 'lodash';
import Snackbar from '../../common/snackBar';
const page_name = 'userManagement';
export interface useCaseStates {
  isUserListLoading: boolean;
  userList: Array<any>;
  userListTotalCount: number;
  userListPageNumber: number;
  enabledUsers: number;
  disabledUsers: number;
  userListError: boolean;
  isAddUserLoading: boolean;
  isAddUserLoadingSuccess: boolean;
  isAddUserError: boolean;
  isUpdateUserLoading: boolean;
  isUpdateUserSuccess: boolean;
  isUpdateUserError: boolean;
  isDeleteUserSuccess: Boolean;
  isDeleteUserError: Boolean;
  userManagementMessage: string;
  isUserAccessListLoading: boolean;
  userAccessList: any;
  userAccessListError: boolean;
  userMgmtSelectedTab: number;
}

const initialState: useCaseStates = {
  isUserListLoading: false,
  userList: [],
  userListTotalCount: 0,
  userListPageNumber: 1,
  enabledUsers: 0,
  disabledUsers: 0,
  userListError: false,
  isAddUserLoading: false,
  isAddUserLoadingSuccess: false,
  isAddUserError: false,
  isUpdateUserLoading: false,
  isUpdateUserSuccess: false,
  isUpdateUserError: false,
  isDeleteUserSuccess: false,
  isDeleteUserError: false,
  userManagementMessage: '',
  isUserAccessListLoading: false,
  userAccessList: {},
  userAccessListError: false,
  userMgmtSelectedTab: 0,
};

const UserManagementReducer = createSlice({
  name: page_name,
  initialState: initialState,
  reducers: {
    getUserList: (state, action: UsersDataRequestActionTypes) => {
      state.isAddUserLoadingSuccess = false;
      state.isUpdateUserSuccess = false;
      state.isDeleteUserSuccess = false;
      state.isUserListLoading = true;
    },
    getUserListResponse: (state, action) => {
      state.isUserListLoading = false;
      state.userList = get(action, 'payload.res.data', []).map((user: any) => {
        return {
          ...user,
          roles: get(user, 'roles', []).filter((role: any) => !['default-roles-iot_in_box'].includes(role.name)),
        };
      });
      state.userListTotalCount = get(action, 'payload.res.total_count', 0);
      state.userListPageNumber = get(action, 'payload.res.page', 0);
      state.enabledUsers = get(action, 'payload.res.enabled_users', 0);
      state.disabledUsers = get(action, 'payload.res.disabled_users', 0);
    },
    getUserListFailed: (state, action: any) => {
      state.isUserListLoading = false;
      state.userListError = true;
      state.userList = [];

      const message = getStatusMessage(page_name, action.payload);
      state.userManagementMessage = message;
      Snackbar.error(message);
    },
    addUserRequest: (state) => {
      state.isUserListLoading = true;
      state.isAddUserLoading = true;
      state.isAddUserLoadingSuccess = false;
      state.isAddUserError = false;
    },
    addUserResponse: (state, action: any) => {
      state.isAddUserLoading = false;
      state.isAddUserLoadingSuccess = true;
      state.isUserListLoading = false;

      const message = getStatusMessage(page_name, action.payload);
      state.userManagementMessage = message;
      Snackbar.success(message);
    },
    addUserRequestFailed: (state, action: any) => {
      state.isAddUserLoading = false;
      state.isAddUserError = true;
      state.isUserListLoading = false;

      const message = getStatusMessage(page_name, action.payload);
      state.userManagementMessage = message;
      Snackbar.error(message);
    },
    updateUserRequest: (state) => {
      state.isUserListLoading = true;
      state.isUpdateUserLoading = true;
      state.isUpdateUserSuccess = false;
      state.isUpdateUserError = false;
    },
    updateUserResponse: (state, action: any) => {
      state.isUpdateUserLoading = false;
      state.isUpdateUserSuccess = true;
      state.isUserListLoading = false;

      const message = getStatusMessage(page_name, action.payload);
      state.userManagementMessage = message;
      Snackbar.success(message);
    },
    updateUserFailed: (state, action: any) => {
      state.isUpdateUserLoading = false;
      state.isUpdateUserError = true;
      state.isUserListLoading = false;

      const message = getStatusMessage(page_name, action.payload);
      state.userManagementMessage = message;
      Snackbar.error(message);
    },
    deleteMultipleUserRequest: (state, action: PayloadAction<UserFormData>) => {
      state.isUserListLoading = true;
      state.isDeleteUserSuccess = false;
      state.isDeleteUserError = false;
    },
    deleteMultipleUserResponse: (state, action: PayloadAction<any>) => {
      state.isUserListLoading = false;
      state.isDeleteUserSuccess = true;
      state.isDeleteUserError = false;

      const message = getStatusMessage(page_name, action.payload);
      state.userManagementMessage = message;
      Snackbar.success(message);
    },
    deleteMultipleUserFailed: (state, action: any) => {
      state.isUserListLoading = false;
      state.isDeleteUserSuccess = false;
      state.isDeleteUserError = true;

      const message = getStatusMessage(page_name, action.payload);
      state.userManagementMessage = message;
      Snackbar.error(message);
    },
    getUserAccessList: (state, action: { payload: string }) => {
      state.isUserAccessListLoading = true;
    },
    getUserAccessListResponse: (state, action) => {
      state.isUserAccessListLoading = false;
      state.userAccessList = action.payload.res;
    },
    getUserAccessListFailed: (state, action: any) => {
      state.isUserAccessListLoading = false;
      state.userAccessListError = true;
      state.userAccessList = {};

      const message = getStatusMessage(page_name, action.payload);
      state.userManagementMessage = message;
      Snackbar.error(message);
    },
    userMgmtTabChange: (state, action) => {
      state.userMgmtSelectedTab = action.payload;
    },
    resetLoadingState: (state) => {
      state.isAddUserLoadingSuccess = false;
      state.isUpdateUserSuccess = false;
      state.isUpdateUserError = false;
      state.isAddUserError = false;
      state.isUserListLoading = false;
      state.isDeleteUserSuccess = false;
      state.isDeleteUserError = false;
      state.userManagementMessage = '';
      state.userListError = false;
      state.isUserAccessListLoading = false;
      state.userAccessListError = false;
    },
    resetUserAccessList: (state) => {
      state.userAccessList = {};
    },
  },
});

export const userManagementActions = UserManagementReducer.actions;
export default UserManagementReducer;
